/**
 * TagManager - Componente Principal de Gestão de Tags
 * Interface completa para gestão de tags com analytics, listagem e operações em lote
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  Tag as TagIcon,
  Plus,
  Search,
  Filter,
  BarChart3,
  Hash,
  Users,
  TrendingUp,
  Edit,
  Trash2,
  Download,
  Upload,
  RefreshCw,
  CheckCircle,
  X
} from 'lucide-react';

import {
  TagsService,
  TagResponse,
  TagAnalytics,
  TagCreate,
  TagUtils,
  BatchOperationResponse,
  CategoriesService,
  CategoryResponse
} from '../services/apiTags.ts';
import TagModal from './TagModal.tsx';
import TagSelector from './TagSelector.tsx';

interface TagManagerProps {
  clinicId: number;
  mode?: 'full' | 'compact' | 'contact';
  contactId?: number;
  maxTags?: number;
  readonly?: boolean;
  onTagsChange?: (tagIds: number[]) => void;
  className?: string;
}

const TagManager: React.FC<TagManagerProps> = ({
  clinicId,
  mode = 'full',
  contactId,
  maxTags = 20,
  readonly = false,
  onTagsChange,
  className = ''
}) => {
  const { isDark } = useTheme();
  // Estado principal
  const [tags, setTags] = useState<TagResponse[]>([]);
  const [categories, setCategories] = useState<CategoryResponse[]>([]);
  const [analytics, setAnalytics] = useState<TagAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estado da UI
  const [view, setView] = useState<'list' | 'analytics' | 'batch'>('list');
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showInactive, setShowInactive] = useState(false);

  // Modal
  const [modalTag, setModalTag] = useState<TagResponse | undefined>();
  const [showModal, setShowModal] = useState(false);

  // Batch operations
  const [batchMode, setBatchMode] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);

  // Carregar tags
  const loadTags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        is_active: showInactive ? undefined : true
      };

      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }

      const tagsData = await TagsService.getTags(clinicId, params);
      setTags(tagsData);

      console.log(`✅ ${tagsData.length} tags carregadas`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao carregar tags';
      setError(errorMessage);
      console.error('❌ Erro ao carregar tags:', error);
    } finally {
      setLoading(false);
    }
  }, [clinicId, selectedCategory, showInactive]);

  // Carregar analytics
  const loadAnalytics = useCallback(async () => {
    if (mode === 'contact') return; // Não carrega analytics no modo contato

    try {
      const analyticsData = await TagsService.getTagAnalytics(clinicId);
      setAnalytics(analyticsData);
      console.log('✅ Analytics carregados');
    } catch (error: any) {
      console.error('❌ Erro ao carregar analytics:', error);
    }
  }, [clinicId, mode]);

  // Carregar categorias
  const loadCategories = useCallback(async () => {
    try {
      const categoriesData = await CategoriesService.getCategoriesWithCount(clinicId);
      setCategories(categoriesData);
      console.log(`✅ ${categoriesData.length} categorias carregadas`);
    } catch (error: any) {
      console.error('❌ Erro ao carregar categorias:', error);
    }
  }, [clinicId]);

  // Carregar dados iniciais
  useEffect(() => {
    loadTags();
    loadCategories();
    if (mode === 'full') {
      loadAnalytics();
    }
  }, [loadTags, loadCategories, loadAnalytics, mode]);

  // Filtrar tags baseado na busca
  const filteredTags = React.useMemo(() => {
    let filtered = [...tags];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(tag =>
        tag.name.toLowerCase().includes(query) ||
        tag.category?.toLowerCase().includes(query) ||
        tag.description?.toLowerCase().includes(query)
      );
    }

    return TagUtils.sortTagsByUsage(filtered);
  }, [tags, searchQuery]);

  // Agrupar tags por categoria
  const tagsByCategory = React.useMemo(() => {
    return TagUtils.groupTagsByCategory(filteredTags);
  }, [filteredTags]);

  // Abrir modal para criar/editar tag
  const handleOpenModal = (tag?: TagResponse) => {
    setModalTag(tag);
    setShowModal(true);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setModalTag(undefined);
  };

  // Salvar tag (criar/editar)
  const handleSaveTag = async (savedTag: TagResponse) => {
    await loadTags(); // Recarregar lista
    await loadCategories(); // Recarregar categorias (caso tenha criado nova)
    if (analytics) {
      await loadAnalytics(); // Recarregar analytics
    }
    handleCloseModal();
  };

  // Criar nova tag rapidamente
  const handleCreateTag = async (tagName: string): Promise<TagResponse> => {
    // Encontrar categoria 'general' ou usar a primeira disponível
    const defaultCategory = categories.find(cat => cat.name === 'general') || categories[0];

    const tagData: TagCreate = {
      name: tagName,
      category: defaultCategory?.name || 'general',
      color: TagUtils.generateRandomColor()
    };

    return await TagsService.createTag(clinicId, tagData);
  };

  // Deletar tag
  const handleDeleteTag = async (tag: TagResponse) => {
    if (!window.confirm(`Tem certeza que deseja excluir a tag "${tag.name}"?`)) {
      return;
    }

    try {
      await TagsService.deleteTag(tag.id);
      await loadTags();
      if (analytics) {
        await loadAnalytics();
      }
      console.log(`✅ Tag "${tag.name}" excluída`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao excluir tag';
      setError(errorMessage);
      console.error('❌ Erro ao excluir tag:', error);
    }
  };

  // Operação em lote
  const handleBatchOperation = async (operation: 'add' | 'remove' | 'replace') => {
    if (!contactId || selectedTags.length === 0) return;

    try {
      setBatchLoading(true);
      setError(null);

      const result: BatchOperationResponse = await TagsService.batchOperationContacts({
        contact_ids: [contactId],
        tag_ids: selectedTags,
        operation
      });

      console.log(`✅ Operação em lote: ${result.success_count} sucessos, ${result.error_count} erros`);

      if (onTagsChange) {
        if (operation === 'replace') {
          onTagsChange(selectedTags);
        } else if (operation === 'add') {
          // Lógica para adicionar tags - apenas passa as tags selecionadas
          // O componente pai deve gerenciar a lógica de adição
          onTagsChange(selectedTags);
        }
      }

      setBatchMode(false);
      setSelectedTags([]);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Erro na operação em lote';
      setError(errorMessage);
      console.error('❌ Erro na operação em lote:', error);
    } finally {
      setBatchLoading(false);
    }
  };

  // Renderizar analytics
  const renderAnalytics = () => {
    if (!analytics) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`border rounded-xl p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Total de Tags</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{analytics.total_tags}</p>
            </div>
            <div className={`p-2 rounded-lg ${isDark ? 'bg-sminy-primary/20 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'}`}>
              <TagIcon className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className={`border rounded-xl p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Total de Usos</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{analytics.total_usage}</p>
            </div>
            <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
              <TrendingUp className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className={`border rounded-xl p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Tags Ativas</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{analytics.active_tags}</p>
            </div>
            <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className={`border rounded-xl p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Média por Tag</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {analytics.total_tags > 0 ? Math.round(analytics.total_usage / analytics.total_tags) : 0}
              </p>
            </div>
            <div className={`p-2 rounded-lg ${isDark ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-50 text-orange-600'}`}>
              <BarChart3 className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Renderizar lista de tags
  const renderTagList = () => {
    return (
      <div className="space-y-4">
        {Object.entries(tagsByCategory).map(([category, categoryTags]) => (
          <div key={category} className={`border rounded-xl p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`}>
            <h3 className={`text-sm font-medium mb-3 capitalize flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <Hash className="w-4 h-4" />
              {category.replace('-', ' ')}
              <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>({categoryTags.length})</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {categoryTags.map(tag => (
                <div
                  key={tag.id}
                  className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${isDark
                    ? 'border-gray-700 hover:border-gray-600 bg-gray-900/50'
                    : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
                    }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full shadow-sm"
                      style={{ backgroundColor: tag.color }}
                    />
                    <div>
                      <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-slate-900'}`}>{tag.name}</p>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{tag.usage_count} usos</p>
                    </div>
                  </div>

                  {!readonly && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenModal(tag)}
                        className={`p-1.5 rounded-lg transition-colors ${isDark
                          ? 'text-gray-400 hover:text-sminy-primary hover:bg-gray-700'
                          : 'text-slate-400 hover:text-sminy-primary hover:bg-slate-100'
                          }`}
                        title="Editar tag"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTag(tag)}
                        className={`p-1.5 rounded-lg transition-colors ${isDark
                          ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700'
                          : 'text-slate-400 hover:text-red-600 hover:bg-slate-100'
                          }`}
                        title="Excluir tag"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {filteredTags.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
            <TagIcon className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-700' : 'text-slate-300'}`} />
            <p className="font-medium">Nenhuma tag encontrada</p>
            <p className="text-sm mt-1">Crie sua primeira tag para começar</p>
          </div>
        )}
      </div>
    );
  };

  // Renderizar topo com controles
  const renderHeader = () => {
    return (
      <div className="flex flex-col xl:flex-row items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 flex-1 w-full xl:w-auto">
          {mode === 'contact' && (
            <h3 className={`text-lg font-medium mr-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Tags do Contato</h3>
          )}

          {view === 'list' && mode !== 'contact' && (
            <div className={`flex items-center gap-3 w-full xl:w-auto p-1.5 rounded-xl transition-all ${isDark ? 'bg-gray-800' : 'bg-white border border-slate-200'
              }`}>
              <div className="relative flex-1 xl:w-64">
                <Search className={`w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar tags..."
                  className={`w-full pl-9 pr-4 py-1.5 text-sm bg-transparent border-none outline-none ${isDark ? 'text-white placeholder-gray-500' : 'text-slate-900 placeholder-slate-400'
                    }`}
                />
              </div>

              <div className={`w-px h-6 ${isDark ? 'bg-gray-700' : 'bg-slate-200'}`} />

              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className={`py-1.5 pl-2 pr-8 text-sm bg-transparent border-none outline-none cursor-pointer ${isDark ? 'text-gray-300' : 'text-slate-600'
                  }`}
              >
                <option value="all">Todas Categorias</option>
                {categories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name.charAt(0).toUpperCase() + category.name.slice(1).replace('-', ' ')}
                  </option>
                ))}
              </select>

              <div className={`w-px h-6 ${isDark ? 'bg-gray-700' : 'bg-slate-200'}`} />

              <label className="flex items-center gap-2 cursor-pointer px-2">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="rounded border-gray-300 text-sminy-primary focus:ring-sminy-primary w-4 h-4"
                />
                <span className={`text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Inativas</span>
              </label>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {/* Modo de visualização */}
          {mode === 'full' && (
            <div className={`flex items-center gap-1 rounded-lg p-1 ${isDark ? 'bg-gray-800' : 'bg-slate-100'}`}>
              <button
                onClick={() => setView('list')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${view === 'list'
                  ? isDark ? 'bg-gray-700 text-white shadow-sm' : 'bg-white text-slate-900 shadow-sm'
                  : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
              >
                Lista
              </button>
              <button
                onClick={() => setView('analytics')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${view === 'analytics'
                  ? isDark ? 'bg-gray-700 text-white shadow-sm' : 'bg-white text-slate-900 shadow-sm'
                  : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
              >
                Analytics
              </button>
            </div>
          )}

          {/* Botões de ação */}
          {!readonly && (
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 px-4 py-2 bg-sminy-primary text-white rounded-xl hover:bg-sminy-primary-hover transition-colors shadow-sm shadow-sminy-primary/20"
            >
              <Plus className="w-4 h-4" />
              Nova Tag
            </button>
          )}

          <button
            onClick={loadTags}
            className={`p-2 rounded-xl transition-colors ${isDark
              ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              }`}
            title="Recarregar"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
    );
  };

  // Modo contato: apenas seletor de tags
  if (mode === 'contact') {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Tags do Contato</h3>
          {!readonly && (
            <button
              onClick={() => handleOpenModal()}
              className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Nova Tag
            </button>
          )}
        </div>

        <TagSelector
          selectedTags={selectedTags}
          onTagsChange={setSelectedTags}
          clinicId={clinicId}
          placeholder="Adicionar tags ao contato..."
          maxTags={maxTags}
          allowCreate={!readonly}
          readonly={readonly}
        />

        {onTagsChange && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onTagsChange(selectedTags)}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
              disabled={selectedTags.length === 0}
            >
              Salvar Tags
            </button>
          </div>
        )}

        <TagModal
          tag={modalTag}
          isOpen={showModal}
          onClose={handleCloseModal}
          onSave={handleSaveTag}
          clinicId={clinicId}
          existingCategories={categories.map(cat => cat.name)}
        />
      </div>
    );
  }

  // Modo completo ou compacto
  return (
    <div className={`space-y-6 ${className}`}>
      {renderHeader()}

      {/* Analytics */}
      {mode === 'full' && view === 'analytics' && renderAnalytics()}

      {/* Filtros e Busca */}
      {/* Filtros e Busca removidos daqui pois foram movidos para o header */}

      {/* Conteúdo principal */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-sminy-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className={`border rounded-lg p-4 ${isDark ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
          ⚠️ {error}
        </div>
      ) : (
        renderTagList()
      )}

      {/* Modal */}
      <TagModal
        tag={modalTag}
        isOpen={showModal}
        onClose={handleCloseModal}
        onSave={handleSaveTag}
        clinicId={clinicId}
        existingCategories={categories.map(cat => cat.name)}
      />
    </div>
  );
};

export default TagManager;